import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiCard, uiEmpty, uiGet, uiHeader, uiKpis, uiMoney, uiPill } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { nmFace } from "./nemesis.js";

/**
 * Offers for You (backend/offers.py): the companies that come to you.
 * It is the second page of the Investments & Offers tab (views/investhub.js).
 *
 *  The Tank             founders pitch to you and four sharks (backend/shark_tank.py):
 *                        ask three questions, bid against the sharks, team up, counter, deal or no deal
 *  Offers on the table   supply contracts, licences, distribution, a strategic stake:
 *                        accept, counter once (they may improve it, or walk), or decline
 *  Buy the whole company buyers for all of it (career.sale_offers): selling is an exit
 *  Signed contracts      what's paying you every month, and for how long
 */

const ofM = (v) => uiMoney(v, { compact: true });

function ofCard(o, month) {
  const left = o.expires - month;
  return `<article class="ofr-card of-${esc(o.kind)}">
    <div class="ofr-card-top"><span class="ofr-icon" aria-hidden="true">${o.icon}</span>
      <div><b>${esc(o.company)}</b><span>${esc(o.label)}</span></div>
      ${left <= 0 ? uiPill("Last month to answer", "warn") : uiPill(`Open until month ${o.expires}`, "info")}</div>
    <p class="ofr-meta">${esc(o.sector)} · ${esc(o.country)}${o.focus ? ` · ${esc(o.focus)}` : ""}</p>
    ${o.channel ? `<div class="ofr-channel">📨 How it came: ${esc(o.channel)}</div>` : ""}
    <blockquote class="ofr-pitch">“${esc(o.pitch)}”</blockquote>
    ${o.concentration_warning ? `<p class="iv-warn">⚠ ${esc(o.concentration_warning)}</p>` : ""}
    <div class="ofr-facts">${o.facts.map((f) => `<div><em>${esc(f.label)}</em><b>${esc(f.value)}</b></div>`).join("")}</div>
    ${o.board_seat ? `<p class="ofr-note">They'd take a seat on your Board.</p>` : ""}
    ${o.control_warning ? `<p class="ofr-warn">⚠ You'd drop under 50%: you'd no longer control the Board.</p>` : ""}
    <div class="ofr-actions">
      <button type="button" class="primary" data-of="${o.id}" data-ans="accept">Accept</button>
      ${o.countered ? "" : `<button type="button" data-of="${o.id}" data-ans="counter">Ask for better terms</button>`}
      <button type="button" class="ofr-quiet" data-of="${o.id}" data-ans="decline">Decline</button>
    </div>
  </article>`;
}

function ofBuyers(d) {
  if (!d.buyers.length) {
    return `<p class="ofr-none">Nobody is bidding for the whole company yet. Buyers come once you've run it for a while and it has a valuation.</p>`;
  }
  return `<div class="ofr-buyers">${d.buyers.map((b) => `<article class="ofr-buyer">
      <div><b>${esc(b.buyer)}</b><span>${esc(b.type)} · ${esc(b.pitch)}</span></div>
      <div class="ofr-facts"><div><em>Price for 100%</em><b>${ofM(b.price)}</b></div><div><em>Premium</em><b>${b.premium_pct >= 0 ? "+" : ""}${b.premium_pct}%</b></div>
        <div><em>You'd walk away with</em><b>${ofM(b.your_payout)}</b></div></div>
      <div class="ofr-actions"><button type="button" class="danger" data-sell-co="${esc(b.buyer)}">Sell the company to ${esc(b.buyer)}</button></div>
    </article>`).join("")}</div>
    <p class="ofr-note">Selling ends this company as an exit: you keep the money and start your next one (CEO Office → Career).</p>`;
}

function ofContracts(d) {
  if (!d.contracts.length) return uiEmpty("No contracts yet", "Accept a supply or licensing offer and it pays you here every month.");
  return `<table class="sx-table"><tr><th>Company</th><th>Kind</th><th class="num">Every month</th><th class="num">Months left</th><th>Status</th></tr>
    ${d.contracts.map((c) => `<tr><td>${esc(c.company)}</td><td>${c.kind === "supply" ? "Supply contract" : "Royalty"}</td>
      <td class="num">${ofM(c.monthly)}</td><td class="num">${c.status === "active" ? c.months_left : "—"}</td>
      <td>${c.status === "active" ? (c.start_month > d.month ? `Starts month ${c.start_month}` : "Paying") : "Ended"}</td></tr>`).join("")}</table>`;
}


// ---------------------------------------------------------------- the Tank (backend/shark_tank.py)

const TK_STATUS = { in: "In", out: "Out", partner: "With you" };

function tkCard(p) {
  return `<article class="tk-card${p.open ? "" : " done"}">
    <div class="tk-card-face">${nmFace(p.face, 74)}</div>
    <div class="tk-card-main">
      <span class="tk-kick">${esc(p.sector)} · ${esc(p.country)}</span>
      <b>${esc(p.name)}</b>
      <span class="tk-what">${esc(p.founder)} makes ${esc(p.what)}.</span>
      <span class="tk-ask">Asking <b>${ofM(p.ask_amount)}</b> for <b>${p.ask_pct}%</b> <em>· values it at ${ofM(p.ask_val)}</em></span>
      ${p.result ? `<span class="tk-res ${p.status}">${esc(p.result)}</span>` : ""}
    </div>
    <button type="button" class="${p.open ? "primary" : ""}" data-tank="${p.id}">${p.open ? (p.stage === "pitch" ? "🦈 Enter the Tank" : "Back into the Tank") : "Watch it again"}</button>
  </article>`;
}

function tkSection(t) {
  const live = t.pitches;
  return `<section class="tk-hero">
    <div class="tk-hero-head"><div><div class="sx-eyebrow">This month on</div><h3>🦈 The Tank</h3>
      <p>Founders pitch to you and four sharks. Ask your questions, then out-bid the sharks, or team up with one.</p></div>
      <div class="tk-sharks-mini">${t.sharks.map((k) => `<span title="${esc(k.name)}, ${esc(k.nick)}">${nmFace(k.face, 40)}</span>`).join("")}</div></div>
    ${live.length ? `<div class="tk-cards">${live.map(tkCard).join("")}</div>`
      : `<p class="ofr-none">No founders this month. New ones walk in most months once you have at least ${ofM(150000)} in cash.</p>`}
    ${t.past.length ? `<details class="tk-past"><summary>Past episodes (${t.past.length}) · ${t.deals_won} deal${t.deals_won === 1 ? "" : "s"} won</summary>
      <ul class="ofr-history">${t.past.map((h) => `<li><i>M${h.month}</i><b>${esc(h.name)}</b><span>${esc(h.result || "The founder left")}</span></li>`).join("")}</ul></details>` : ""}
  </section>`;
}

function tkSeat(s, r) {
  const o = s.offer;
  const tag = s.status === "partner" ? "With you" : o && s.status === "in" ? `${ofM(o.amount)} for ${o.pct}%` : TK_STATUS[s.status];
  return `<div class="tk-seat ${s.status}${o && s.status === "in" ? " offer" : ""}">
    <div class="tk-seat-face">${nmFace(s.face, 64)}</div>
    <b>${esc(s.name)}</b><em>${esc(s.nick)}</em>
    <span class="tk-seat-tag">${esc(tag)}${o && o.royalty && s.status === "in" ? " + royalty" : ""}</span>
    ${o && s.status === "in" ? `<span class="tk-seat-val">values it at ${ofM(s.offer_val)}</span>` : ""}
  </div>`;
}

function tkLine(l, r) {
  const shark = r.sharks.find((s) => s.key === l.who);
  const who = l.who === "you" ? "You" : l.who === "founder" ? r.founder.split(" ")[0] : shark ? shark.name : "";
  return `<li class="tk-l ${shark ? "shark" : l.who}">${who ? `<b>${esc(who)}</b>` : ""}<span>${esc(l.text)}</span></li>`;
}

function tkControls(r) {
  if (!r.open) {
    const tone = r.status === "won" ? "won" : r.status === "lost" ? "lost" : "none";
    return `<div class="tk-end ${tone}"><div class="tk-end-big">${r.status === "won" ? "🤝 DEAL!" : r.status === "lost" ? "They chose a shark" : "No deal"}</div>
      <p>${esc(r.result || "The founder has left the Tank.")}</p>
      ${r.status === "won" ? `<button type="button" class="primary" data-tk-go>See it in Corporate Investments →</button>` : ""}</div>`;
  }
  const inSharks = r.sharks.filter((s) => s.status === "in");
  const best = inSharks.filter((s) => s.offer).sort((a, b) => b.offer_val - a.offer_val)[0];
  const qs = r.stage === "pitch" || r.stage === "questions"
    ? `<div class="tk-qs"><h4>Ask a question <em>${r.questions_left} left</em></h4>
        <div class="tk-qgrid">${r.questions.map((q) => `<button type="button" data-tk-q="${q.key}" ${q.asked || !r.questions_left ? "disabled" : ""}>${esc(q.text)}</button>`).join("")}</div></div>` : "";
  const maxAmt = Math.max(r.ask_amount * 2, 50000);
  return `${qs}
    ${r.counter ? `<div class="tk-counter"><span>${esc(r.founder.split(" ")[0])} countered: <b>${ofM(r.counter.amount)} for ${r.counter.pct}%</b></span>
      <button type="button" class="primary" data-tk-counter>Accept the counter</button></div>` : ""}
    <div class="tk-offer">
      <h4>Your offer <em>round ${Math.min(r.round + 1, r.rounds_max)} of ${r.rounds_max}${r.round + 1 >= r.rounds_max ? " · final offers" : ""}</em></h4>
      <label class="mk-label">Cash <b id="tk-amt"></b></label>
      <input type="range" id="tk-amt-r" min="${Math.round(r.ask_amount * 0.25 / 5000) * 5000 || 10000}" max="${Math.round(maxAmt / 5000) * 5000}" step="5000" value="${(r.you && r.you.amount) || r.ask_amount}" aria-label="Cash you offer">
      <label class="mk-label">For equity <b id="tk-pct"></b></label>
      <input type="range" id="tk-pct-r" min="1" max="60" step="0.5" value="${(r.you && r.you.pct) || r.ask_pct}" aria-label="Equity you ask for">
      <p class="tk-read" id="tk-read"></p>
      ${inSharks.length ? `<label class="mv-pick">Bid<select id="tk-partner"><option value="">Alone</option>${inSharks.map((s) => `<option value="${s.key}">Team up with ${esc(s.name)} (you each put in half)</option>`).join("")}</select></label>` : ""}
      <div class="tk-go"><button type="button" class="primary" data-tk-offer>Make the offer</button><button type="button" class="ofr-quiet" data-tk-out>I'm out</button></div>
      ${best ? `<p class="tk-best">Best shark offer: ${esc(best.name)}, ${ofM(best.offer.amount)} for ${best.offer.pct}% (values it at ${ofM(best.offer_val)}).</p>` : ""}
    </div>`;
}

async function openTankRoom(id) {
  const back = document.createElement("div");
  back.className = "modal-backdrop";
  back.innerHTML = `<div class="modal modal-page tk-room" role="dialog" aria-label="The Tank"><div class="lx-loading">Walking into the Tank…</div></div>`;
  document.body.appendChild(back);
  const box = back.querySelector(".tk-room");
  let dirty = false;
  const close = async () => { back.remove(); if (dirty && window.refreshAll) await window.refreshAll(); };
  back.addEventListener("click", (e) => { if (e.target === back) close(); });
  const act = async (type, params) => {
    box.querySelectorAll("button").forEach((b) => { b.disabled = true; });
    try { const r = await postJSON("/actions/execute", { action: { type, params: { id, ...params } } }); dirty = true; if (r.message) toast(r.message); }
    catch (e) { toast(e.message, true); }
    await paint();
  };
  const paint = async () => {
    let r;
    try { r = await uiGet(`/tank/${id}`); } catch (e) { box.innerHTML = `<p class="neg" style="padding:20px;">${esc(e.message)}</p>`; return; }
    box.innerHTML = `
      <button type="button" class="modal-x" data-x aria-label="Close">✕</button>
      <div class="tk-top">
        <div class="tk-row">${r.sharks.map((s) => tkSeat(s, r)).join("")}
          <div class="tk-seat you${r.you ? " offer" : ""}"><div class="tk-seat-face tk-you">YOU</div><b>You</b><em>${r.fit ? "a strategic fit" : "corporate investor"}</em>
            <span class="tk-seat-tag">${r.you ? `${ofM(r.you.amount)} for ${r.you.pct}%` : "In"}</span></div></div>
        <div class="tk-founder">
          <div class="tk-founder-face">${nmFace(r.face, 96)}</div>
          <div><span class="tk-kick">${esc(r.sector)} · ${esc(r.country)}</span><h3>${esc(r.name)}</h3>
            <p>${esc(r.founder)} makes ${esc(r.what)}.</p>
            <div class="tk-ask-big">${ofM(r.ask_amount)} <span>for</span> ${r.ask_pct}% <em>values it at ${ofM(r.ask_val)}</em></div></div>
          <div class="tk-board">${r.board.map((b) => `<div><em>${esc(b.label)}</em><b>${esc(b.value)}</b></div>`).join("")}
            <p class="tk-star">*as the founder tells it</p></div>
        </div>
        ${r.revealed.length ? `<ul class="tk-reveals">${r.revealed.map((x) => `<li>🔎 ${esc(x)}</li>`).join("")}</ul>` : ""}
      </div>
      <div class="tk-mid">
        <ol class="tk-log">${r.log.filter((l) => l.who !== "reveal").map((l) => tkLine(l, r)).join("")}</ol>
        <div class="tk-act">${tkControls(r)}</div>
      </div>`;
    const log = box.querySelector(".tk-log");
    log.scrollTop = log.scrollHeight;
    box.querySelector("[data-x]").addEventListener("click", close);
    box.querySelectorAll("[data-tk-q]").forEach((b) => b.addEventListener("click", () => act("tank_ask", { q: b.dataset.tkQ })));
    box.querySelector("[data-tk-counter]")?.addEventListener("click", () => act("tank_accept_counter", {}));
    box.querySelector("[data-tk-out]")?.addEventListener("click", () => { if (confirm("Go out? The sharks fight over it without you.")) act("tank_out", {}); });
    box.querySelector("[data-tk-go]")?.addEventListener("click", () => { close(); if (window.gesInvestPage) window.gesInvestPage("investments"); window.ges && window.ges.showTab("investments"); });
    const ar = box.querySelector("#tk-amt-r"), pr = box.querySelector("#tk-pct-r");
    if (ar && pr) {
      const read = () => {
        const amt = +ar.value, pct = +pr.value, val = (100 * amt) / pct;
        const partner = box.querySelector("#tk-partner");
        box.querySelector("#tk-amt").textContent = ofM(amt);
        box.querySelector("#tk-pct").textContent = `${pct}%`;
        const vs = val >= r.ask_val ? "at or above what they asked" : `${Math.round(100 - (100 * val) / r.ask_val)}% below what they asked`;
        box.querySelector("#tk-read").innerHTML = `Values ${esc(r.name)} at <b>${ofM(val)}</b> (${vs}).${partner && partner.value ? ` You'd put in ${ofM(amt / 2)} for ${(pct / 2).toFixed(1)}%.` : ""} You have ${ofM(r.cash)}.`;
      };
      ar.addEventListener("input", read); pr.addEventListener("input", read);
      box.querySelector("#tk-partner")?.addEventListener("change", read);
      read();
      box.querySelector("[data-tk-offer]").addEventListener("click", () => act("tank_offer", { amount: +ar.value, pct: +pr.value, partner: (box.querySelector("#tk-partner") || {}).value || null }));
    }
  };
  await paint();
}

window.openTankRoom = openTankRoom;

async function renderOffers(main) {
  const [d, tank] = await Promise.all([uiGet("/offers"), uiGet("/tank").catch(() => null)]);
  const stateWord = { accepted: "Accepted", declined: "Declined", withdrawn: "They withdrew", expired: "Expired" };
  main.innerHTML = `
    <div class="sx ofr-page">
      ${uiHeader("Offers for You", "Companies that come to you: buyers for your products, licences for your brand, partners who want a stake, and buyers for the whole company.")}
      ${uiKpis([
        { label: "Offers waiting", value: String(d.offers.length), tone: d.offers.length ? "pos" : "" },
        { label: "Contract income", value: `${ofM(d.monthly_contract_revenue)}/mo`, sub: "from signed offers" },
        { label: "You own", value: `${d.you_own.toFixed(1)}%`, sub: d.you_own > 50 ? "you control the Board" : "under half" },
        { label: "Buyers for the company", value: String(d.buyers.length) },
      ])}
      ${d.has_products ? "" : `<div class="ofr-first">
          <b>🚀 First, launch a product.</b><span>Companies only come knocking once you sell something. Launch your first product and the offers start next month.</span>
          <button type="button" class="primary" data-go-products>Launch your first product →</button></div>`}
      ${tank ? tkSection(tank) : ""}
      <section><h3 class="ofr-h">Offers on the table</h3>
        ${d.offers.length ? `<div class="ofr-grid">${d.offers.map((o) => ofCard(o, d.month)).join("")}</div>`
          : uiEmpty("No offers this month", d.has_products ? "New companies get in touch most months. Keep growing and they'll call." : "Launch a product first.")}
      </section>
      ${uiCard("Who wants to buy your whole company", ofBuyers(d), { sub: "Real bids for 100% of the company" })}
      <div class="sx-grid-2">
        ${uiCard("Signed contracts", ofContracts(d), { sub: "Revenue that arrives every month" })}
        ${uiCard("Past offers", d.history.length ? `<ul class="ofr-history">${d.history.map((h) => `<li><i>M${h.month}</i><b>${esc(h.company)}</b><span>${esc(h.kind)}</span><em>${esc(stateWord[h.status] || h.status)}</em></li>`).join("")}</ul>`
          : uiEmpty("Nothing yet", "Every offer you answer is kept here."))}
      </div>
      <p class="lx-note">${esc(d.how)}</p>
    </div>`;
  main.querySelectorAll("[data-tank]").forEach((b) => b.addEventListener("click", () => openTankRoom(+b.dataset.tank)));
  main.querySelector("[data-go-products]")?.addEventListener("click", () => window.ges && window.ges.showTab("products"));
  main.querySelectorAll("[data-of]").forEach((b) => b.addEventListener("click", async () => {
    const ans = b.dataset.ans;
    if (ans === "decline" && !confirm("Decline this offer?")) return;
    b.disabled = true;
    try {
      const r = await postJSON("/actions/execute", { action: { type: "offer_respond", params: { offer_id: +b.dataset.of, answer: ans } } });
      toast(r.message);
      await window.refreshAll();
    } catch (e) { b.disabled = false; toast(e.message, true); }
  }));
  main.querySelectorAll("[data-sell-co]").forEach((b) => b.addEventListener("click", async () => {
    if (!confirm(`Sell the whole company to ${b.dataset.sellCo}? This ends this company as an exit.`)) return;
    try {
      const r = await postJSON("/actions/execute", { action: { type: "career_sell_company", params: { buyer: b.dataset.sellCo } } });
      toast(r.message);
      await window.refreshAll();
    } catch (e) { toast(e.message, true); }
  }));
}

window.renderOffers = renderOffers;

export { openTankRoom, renderOffers };
